import { type BillingApi } from '@/features/billing/api/billing-api'
import { collection } from '@/infra/collection'
import {
  BillingProfile,
  type BillingProfileProps,
} from '@/features/billing/profile/BillingProfile'
import DataLoader from 'dataloader'
import {
  getWorkspaceMemberCompositeIdCacheKey,
  type WorkspaceMemberCompositeId,
} from '@/features/iam'
import { type BillingProfileResource } from '@/features/billing/api/resources/billing-profile'
import { Id } from '@/infra/id'
import { safeParseDate } from '@/utils/date-util'
import type {
  BillingMethodResource,
  CreateBillingMethodRequest,
  SetupIntentResource,
} from '@/features/billing/api/resources/billing-method'
import {
  type BillingMethod,
  type BillingMethodProps,
} from '@/features/billing/profile/BillingMethod'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { SetupIntent } from './SetupIntent'

export class BillingProfileStore {
  readonly profiles = collection<BillingProfile>({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new BillingProfile(),
  })

  private readonly _profileLoader = new DataLoader(
    (ids: readonly WorkspaceMemberCompositeId[]) =>
      Promise.all(ids.map(this._fetchProfileCore.bind(this))),
    { cacheKeyFn: getWorkspaceMemberCompositeIdCacheKey },
  )

  constructor(private readonly _api: BillingApi) {}

  /**
   * Gets the billing profile for the specified member.
   * @param member
   */
  getBillingProfileForMember(member: WorkspaceMember) {
    return (
      this.profiles.items.find(
        (p) =>
          p.userId === member.userId && p.workspaceId === member.workspaceId,
      ) ?? BillingProfile.none
    )
  }

  /**
   * Fetches a billing profile for the workspace member.
   *
   * @param member
   */
  fetchBillingProfileForMember(member: WorkspaceMember) {
    return this._profileLoader.load(member.toCompositeId())
  }

  /**
   * Makes the specified billing method the default of the profile it
   * belongs to.
   *
   * @param method
   */
  async changeDefaultMethod(method: BillingMethod) {
    const { profile, id } = method
    const resource = await this._api.profiles.updateMemberBillingProfile(
      Id.assert(profile.workspaceId),
      Id.assert(profile.userId),
      {
        default_billing_method_id: id,
      },
    )

    const props = mapProfileResourceToProps(resource)
    this.profiles.set(props)
  }

  /**
   * Creates a card billing method using a Stripe token.
   */
  async createCardBillingMethodForMember(
    member: WorkspaceMember,
    token: string,
  ) {
    const profile = await this.fetchBillingProfileForMember(member)
    const payload: CreateBillingMethodRequest = {
      type: 'Stripe',
      user_id: profile.userId,
      workspace_id: Id.assert(profile.workspaceId),
      token,
    }

    const resource = await this._api.profiles.addBillingMethod(payload)
    return profile.billingMethods.set(mapBillingMethodResourceToProps(resource))
  }

  /**
   * Sets up a Stripe Payment Method Intent for the given member.
   */
  async setupStripePaymentMethodIntent(member: WorkspaceMember) {
    const resource = await this._api.profiles.setupStripePaymentMethodIntent({
      workspace_id: Id.assert(member.workspaceId),
      user_id: Id.assert(member.userId),
    })

    return mapSetupIntentResourceToSetupIntent(resource)
  }

  /**
   * Completes the setup of a Stripe Payment Method Intent.
   */
  async completeSetupStripePaymentMethodIntent(
    setupIntentId: string,
    member: WorkspaceMember,
  ) {
    const profile = await this.fetchBillingProfileForMember(member)

    const resource =
      await this._api.profiles.completeSetupStripePaymentMethodIntent(
        setupIntentId,
      )
    return profile.billingMethods.set(mapBillingMethodResourceToProps(resource))
  }

  /**
   * Removes the billing method from the profile.
   */
  async removeBillingMethod(method: BillingMethod) {
    await this._api.profiles.removeBillingMethod(Id.assert(method.id))
    method.profile.billingMethods.remove(method)
  }

  /**
   * Whether the given member has a billing method.
   */
  async checkHasBillingMethod(member: WorkspaceMember) {
    await this.fetchBillingProfileForMember(member)

    return this.profiles.some(
      (x) =>
        x.workspaceId === member.workspaceId &&
        x.userId === member.userId &&
        x.billingMethods.length > 0,
    )
  }

  private async _fetchProfileCore(id: WorkspaceMemberCompositeId) {
    const resource = await this._api.profiles.getMemberBillingProfile(
      id.workspaceId,
      id.userId,
    )
    const props = mapProfileResourceToProps(resource)
    return this.profiles.set(props)
  }
}

function mapSetupIntentResourceToSetupIntent(
  resource: SetupIntentResource,
): SetupIntent {
  return {
    setupIntentId: resource.setup_intent_id,
    clientSecret: resource.client_secret,
  }
}

function mapProfileResourceToProps(
  resource: BillingProfileResource,
): BillingProfileProps {
  return {
    id: Id(resource.id),
    userId: Id(resource.user_public_id),
    workspaceId: Id(resource.workspace_id),
    teamId: Id(resource.team_id),
    referralCouponId: Id(resource.referral_coupon_id),
    referralCouponCode: resource.referral_coupon_code,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
    defaultBillingMethodId: Id(resource.default_billing_method_id),
    billingMethods: resource?.billing_methods.map(
      mapBillingMethodResourceToProps,
    ),
  }
}

function mapBillingMethodResourceToProps(
  resource: BillingMethodResource,
): BillingMethodProps {
  return {
    id: Id(resource.id),
    dateCreated: safeParseDate(resource.date_created),
    type:
      resource.type === 'Stripe'
        ? {
            type: 'Stripe',
            last4: resource.last4,
            expMonth: resource.exp_month,
            expYear: resource.exp_year,
            brand: resource.brand,
          }
        : resource.type === 'Credit'
          ? {
              type: 'Credit',
              balance: resource.balance,
              limit: resource.limit,
            }
          : resource.type === 'StripeCardPaymentMethod'
            ? {
                type: 'StripeCardPaymentMethod',
                last4: resource.last4,
                expMonth: resource.exp_month,
                expYear: resource.exp_year,
                brand: resource.brand,
              }
            : { type: 'Unknown' },
  }
}
